import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';
import Sources from '../../components/Sources';
import FAQSection from '../../components/FAQSection';
import TableOfContents from '../../components/TableOfContents';
import Callout from '../../components/Callout';
import Breadcrumbs from '../../components/Breadcrumbs';

export default function LilleTrailer() {
  const breadcrumbs = [
    { name: "Forside", path: "/" },
    { name: "Syn og udstyr", path: "/trailer-syn" },
    { name: "Lille trailer", path: "/lille-trailer" }
  ];
  const tocItems = [
    { id: "hvad-er-en-lille-trailer", title: "Hvad kendetegner en lille trailer (350–500 kg)?" },
    { id: "koerekortregler", title: "Kørekort: Hvad må du trække med almindeligt B-kort?" },
    { id: "bilens-graenser", title: "Hvor meget må bilen trække uden bremser?" },
    { id: "fordele-ved-500-kg", title: "De økonomiske fordele: Ingen syn og 0 kr. i vægtafgift" },
    { id: "tjekliste-og-lastning", title: "Sikkerhed og den vigtige sikkerhedsstrop" }
  ];

  const faq = [
    {
      q: "Må jeg køre med en 500 kg trailer på et almindeligt B-kørekort?",
      a: "Ja, altid. Med et almindeligt kategori B-kørekort må du altid tilkoble en trailer med en tilladt totalvægt på op til 750 kg, uanset hvor stor eller tung din bil er (op til bilens max 3.500 kg totalvægt). En trailer på 350–500 kg kræver aldrig trailerkørekort."
    },
    {
      q: "Skal en 500 kg trailer synes ved ejerskifte?",
      a: "Nej. Påhængskøretøjer med en tilladt totalvægt på 500 kg eller derunder er fuldstændigt fritaget for periodisk syn og skal heller aldrig synes i forbindelse med ejerskifte eller omregistrering."
    },
    {
      q: "Hvor meget må man læsse på en lille 500 kg trailer?",
      a: "En typisk 500 kg ubremset havevogn har en egenvægt på mellem 100 og 140 kg. Det giver en lovlig lasteevne på ca. 360–400 kg. Læs altid det præcise tal på typeskiltet på trailerens vange."
    },
    {
      q: "Må min elbil eller mikrobil trække en 500 kg trailer?",
      a: "Det afhænger af bilens registreringsattest under punktet 'Maksimal påhængsvognsvægt uden bremser'. Visse mikrobiler og elbiler må kun trække 300–450 kg eller har slet intet godkendt træk. Kontrollér altid bilens attest forud for tilkobling."
    }
  ];

  const sources = [
    { title: "Færdselsstyrelsen: Kørekort til påhængskøretøj til bil", source: "fstyr.dk", url: "https://fstyr.dk/privat/koerekort/udvidelse-af-koerekort/koerekort-til-paahaengskoeretoej-til-bil" },
    { title: "Motorstyrelsen: Regler for trailer og påhængsvogn", source: "motorst.dk", url: "https://motorst.dk/borger/nummerplader/trailer" },
    { title: "Færdselsloven § 43 og § 68 (LBK nr. 1324 af 18/09/2023)", source: "retsinformation.dk", url: "https://www.retsinformation.dk/eli/lta/2023/1324" }
  ];

  return (
    <>
      <SEO 
        title="Lille trailer (350–500 kg): Regler, vægt og kørekort" 
        description="Alt om små trailere (350–500 kg): hvorfor B-kørekort altid er nok, 0 kr. i vægtafgift, intet syn ved ejerskifte og regler for ubremsede vogne." 
        pathname="/lille-trailer"
        faq={faq}
        breadcrumbs={breadcrumbs}
      />

      <article>
        <Breadcrumbs items={breadcrumbs} />
        <h1 className="text-[30px] sm:text-[36px] font-bold text-[#2E3A46] tracking-tight leading-tight mb-4">
          Lille trailer (350–500 kg): Regler, vægt og kørekort
        </h1>

        <p className="text-[18px] text-[#1A1C1F] leading-relaxed mb-6 font-medium">
          En lille trailer med en tilladt totalvægt på <strong>350 til 500 kg må altid føres med et almindeligt B-kørekort</strong>. Den kræver aldrig trailerkørekort (BE), uanset hvilken personbil den spændes efter.
        </p>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          Derudover nyder trailere op til 500 kg godt af markante administrative fordele i Danmark: <strong>0 kr. i årlig vægtafgift</strong> og <strong>intet krav om syn</strong> ved ejerskifte. Det gør den ubremsede 500 kg trailer til Danmarks mest populære model til haveaffald og byggemarkedsture.
        </p>

        <TableOfContents items={tocItems} />

        <Callout title="De 4 største fordele ved en lille trailer (&le; 500 kg)">
          <ul className="m-0 p-0 list-none space-y-1 font-mono text-[15px]">
            <li>1. Almindeligt B-kørekort er altid fuldt tilstrækkeligt.</li>
            <li>2. Årlig vægtafgift til staten er 0 kr.</li>
            <li>3. Skal aldrig til syn – hverken periodisk eller ved videresalg.</li>
            <li>4. Lave vedligeholdelsesomkostninger uden slidte bremsekabler.</li>
          </ul>
        </Callout>

        <h2 id="hvad-er-en-lille-trailer" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Hvad kendetegner en lille trailer (350–500 kg)?
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          Små trailere i vægtklassen 350, 400, 450 og 500 kg er konstrueret som <strong>ubremsede påhængsvogne</strong> (uden påløbsbremse). Det betyder, at det alene er bilens bremser, der skal standse hele vogntoget under opbremsning.
        </p>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          Trailerens opbygning er enkel: en galvaniseret stålramme, en stiv torsionsaksel med gummifjedring, to 13-tommer hjul (typisk 145/80 R13) og en kuglekobling med en sikkerhedswire.
        </p>

        <h2 id="koerekortregler" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Kørekort: Hvad må du trække med almindeligt B-kort?
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          I henhold til Færdselsstyrelsens bekendtgørelse om kørekort gælder en fast hovedregel:
        </p>

        <div className="my-6 p-4 border border-[#E4E1D8] bg-[#FBFAF6] rounded-[4px] space-y-2">
          <p className="text-[16px] font-bold text-[#2E3A46] m-0 font-mono">
            750 kg-reglen for kategori B:
          </p>
          <p className="text-[15px] text-[#1A1C1F] m-0">
            Med et kategori B-kørekort må du altid køre med en bil med tilladt totalvægt på op til 3.500 kg tilkoblet en påhængsvogn med en tilladt totalvægt på <strong>højst 750 kg</strong>. Vogntogets samlede tilladte vægt må i denne kombination nå op på <strong>4.250 kg</strong>.
          </p>
        </div>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          Da en trailer på 350–500 kg ligger langt under 750 kg-loftet, opstår der <em>aldrig</em> kørekortmæssige problemer med et almindeligt bilkørekort. Læs mere i <Link to="/koerekort-og-trailer" className="text-[#2E3A46] font-medium hover:text-[#B58200] hover:underline underline-offset-2">kørekort og trailer</Link> samt oversigten over <Link to="/hvor-meget-maa-man-traekke" className="text-[#2E3A46] font-medium hover:text-[#B58200] hover:underline underline-offset-2">hvor meget man må trække</Link>.
        </p>

        <h2 id="bilens-graenser" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Hvor meget må bilen trække uden bremser?
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          Selvom dit kørekort tillader kørslen, skal du altid kontrollere <strong>bilens tekniske begrænsning</strong> i bilens registreringsattest:
        </p>

        <div className="my-6 overflow-x-auto border-t border-[#B58200]">
          <table className="w-full text-left text-[15px] border-collapse">
            <thead>
              <tr className="border-b border-[#E4E1D8] font-mono text-[13px] uppercase text-[#2E3A46]">
                <th className="py-2.5 pr-4 font-semibold">Biltype (eksempler)</th>
                <th className="py-2.5 pr-4 font-semibold">Typisk max ubremset vægt</th>
                <th className="py-2.5 font-semibold">Må den trække 500 kg trailer?</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E4E1D8] tabular-nums">
              <tr>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">Mikrobil (f.eks. Aygo, Up)</td>
                <td className="py-3 pr-4">0 – 400 kg</td>
                <td className="py-3 text-[#5C636B]">Kun hvis faktisk vægt holder sig under bilens tal</td>
              </tr>
              <tr>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">Mellemklassebil (f.eks. Golf, Focus)</td>
                <td className="py-3 pr-4">600 – 680 kg</td>
                <td className="py-3 font-semibold text-[#2E3A46]">Ja, fuldt lovligt fuldt læsset</td>
              </tr>
              <tr>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">Stor SUV / Stationcar</td>
                <td className="py-3 pr-4 font-bold text-[#B58200]">750 kg (maksimum)</td>
                <td className="py-3 font-semibold text-[#2E3A46]">Ja, fuldt lovligt</td>
              </tr>
              <tr>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">Mindre elbil</td>
                <td className="py-3 pr-4">0 – 500 kg</td>
                <td className="py-3 text-[#5C636B]">Kræver fabriksmonteret anhængertræk-godkendelse</td>
              </tr>
            </tbody>
          </table>
        </div>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          Husk, at du aldrig må overlæsse traileren, så dens faktiske vægt overstiger bilens koblingsvægt uden bremser. Se forklaringen på <Link to="/trailer-totalvaegt" className="text-[#2E3A46] font-medium hover:text-[#B58200] hover:underline underline-offset-2">trailer totalvægt, egenvægt og lasteevne</Link>.
        </p>

        <h2 id="fordele-ved-500-kg" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          De økonomiske fordele: Ingen syn og 0 kr. i vægtafgift
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          De to største grunde til, at 500 kg-klassen er Danmarks mest solgte trailertype, er:
        </p>

        <ol className="list-decimal list-inside space-y-2 mb-6 text-[#1A1C1F]">
          <li><strong>0 kr. i vægtafgift:</strong> I henhold til vægtafgiftsloven opkræves der ingen årlig afgift for trailere med tilladt totalvægt op til 500 kg. Vejer traileren 501 kg, koster det årlig afgift (se <Link to="/trailer-vaegtafgift" className="text-[#2E3A46] font-medium hover:text-[#B58200] hover:underline underline-offset-2">trailer vægtafgift</Link>).</li>
          <li><strong>Ingen syn ved ejerskifte:</strong> Mens trailere over 500 kg skal synes ved salg, hvis de er ældre end 5 år, skal en 500 kg trailer <em>aldrig</em> til syn ved ejerskifte (se <Link to="/trailer-syn" className="text-[#2E3A46] font-medium hover:text-[#B58200] hover:underline underline-offset-2">syn af trailer</Link>). Du omregistrerer blot direkte på nettet for 340 kr.</li>
        </ol>

        <h2 id="tjekliste-og-lastning" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Sikkerhed og den vigtige sikkerhedsstrop
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          På en ubremset trailer kaldes wiren en <strong>sikkerhedsstrop</strong> (eller hjælpeforbindelse).
        </p>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          I modsætning til en katastrofewire på en bremset trailer, som skal udløse en nødbremse og derefter knække, har stroppen på en ubremset vogn til formål at <strong>holde trailerens trækstang oppe fra vejbanen</strong>, hvis kuglekoblingen skulle slippe kuglen. Dermed undgås det, at traileren graver sig ned i asfalten og laver en livsfarlig kolbøtte.
        </p>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          Sørg altid for korrekt <Link to="/kugletryk" className="text-[#2E3A46] font-medium hover:text-[#B58200] hover:underline underline-offset-2">kugletryk (ca. 50 kg)</Link> og forsvarlig fastsurring af gods (se guiden <Link to="/lastsikring" className="text-[#2E3A46] font-medium hover:text-[#B58200] hover:underline underline-offset-2">lastsikring på trailer</Link>).
        </p>

        <FAQSection items={faq} />
        <Sources links={sources} />
      </article>
    </>
  );
}
